import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, User, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/lib/types/auth';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { toast } from 'react-hot-toast';

interface UserData {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  roles?: string[];
}

interface UserSelectorProps {
  selectedUserId: string | null;
  onUserSelect: (userId: string | null, userData: UserData | null) => void;
  className?: string;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  selectedUserId,
  onUserSelect,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  // Check if current user is Master Admin, CS Admin, or Marketing Admin
  const isMasterAdmin = currentUser?.roles?.includes(UserRole.MasterAdmin);
  const isCSAdmin = currentUser?.roles?.includes(UserRole.CSAdmin);
  const isMarketingAdmin = currentUser?.roles?.includes(UserRole.MarketingAdmin);
  const canViewOtherUsers = isMasterAdmin || isCSAdmin || isMarketingAdmin;

  useEffect(() => {
    if (canViewOtherUsers) {
      loadUsers();
    }
  }, [canViewOtherUsers]);

  useEffect(() => {
    if (selectedUserId && users.length > 0) {
      const user = users.find(u => u.id === selectedUserId);
      setSelectedUser(user || null);
    } else {
      setSelectedUser(null);
    }
  }, [selectedUserId, users]);

  const loadUsers = async () => {
    if (!canViewOtherUsers) return;

    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('displayName', 'asc'));
      const snapshot = await getDocs(q);
      
      const userData: UserData[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        
        // Filter out deleted users
        if (data.status === 'deleted') {
          return; // Skip deleted users
        }

        const displayName = data.displayName || data.email || 'Unknown User';
        
        userData.push({
          id: doc.id,
          email: data.email || '',
          displayName: displayName,
          photoURL: data.photoURL,
          roles: data.roles || []
        });
      });
      
      setUsers(userData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user: UserData | null) => {
    setSelectedUser(user);
    onUserSelect(user?.id || null, user);
    setIsOpen(false);
  };

  // Don't render if not Master Admin or Marketing Admin
  if (!canViewOtherUsers) {
    return null;
  }

  // Determine the title based on role
  const buttonTitle = isMasterAdmin 
    ? "Master Admin User Visibility - View any user's tasks"
    : "User Visibility - View any user's tasks";

  const popup = isOpen
    ? ReactDOM.createPortal(
        <AnimatePresence>
          <div className="user-selector-popup fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            {/* Popup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="relative w-80 bg-slate-800/95 backdrop-blur-xl border border-slate-600/50 rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-600/50">
                <span className="text-sm font-semibold text-white">View User's Tasks</span>
                <button onClick={() => setIsOpen(false)} className="p-1 rounded-md hover:bg-slate-700/50 text-white/60 hover:text-white transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-2">
                {/* My Tasks Option */}
                <button
                  onClick={() => handleUserSelect(null)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    !selectedUser
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : 'hover:bg-slate-700/50 text-white/90'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    {currentUser?.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt={currentUser.displayName || currentUser.email}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">Mine</div>
                    <div className="text-xs text-white/60 truncate">
                      {currentUser?.displayName || currentUser?.email}
                    </div>
                  </div>
                </button>
              </div>

              {/* Divider */}
              <div className="h-px bg-slate-600/50 mx-2" />

              {/* Users List */}
              <div className="max-h-72 overflow-y-auto p-2">
                {users.length === 0 ? (
                  <div className="px-3 py-4 text-center text-white/60 text-sm">
                    No users found
                  </div>
                ) : (
                  users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                        selectedUser?.id === user.id
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          : 'hover:bg-slate-700/50 text-white/90'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-slate-600 to-slate-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{user.displayName}</div>
                        <div className="text-xs text-white/60 truncate">{user.email}</div>
                        {user.roles && user.roles.length > 0 && (
                          <div className="text-xs text-emerald-400/80 truncate">
                            {user.roles.join(', ')}
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )
    : null;

  return (
    <div className={className}>
      {/* User Visibility Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="relative group"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        title={buttonTitle}
      >
        <div className="relative w-10 h-10 rounded-lg bg-gray-900 hover:bg-gray-800 shadow-md flex items-center justify-center border border-gray-700 overflow-hidden transition-colors duration-200">
          <div className="relative z-10 w-full h-full flex items-center justify-center">
            {selectedUser ? (
              selectedUser.photoURL ? (
                <img
                  src={selectedUser.photoURL}
                  alt={selectedUser.displayName}
                  className="w-6 h-6 rounded-full object-cover border border-gray-600"
                />
              ) : (
                <User className="w-4 h-4 text-white" />
              )
            ) : (
              <Users className="w-4 h-4 text-white" />
            )}
          </div>

          {loading && (
            <div className="absolute inset-0 bg-black/30 rounded-lg flex items-center justify-center">
              <div className="w-3 h-3 border border-white/60 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </motion.button>

      {popup}
    </div>
  );
};

export default UserSelector;